/**
 * `AuthenticationFlow` — orchestrator that drives the auth + KEK
 * release state machine against `IEnvelopeCryptoAdapter` +
 * `IProjectionServiceClient` + `IActiveSession`.
 *
 * Composition contract:
 *
 *   IEnvelopeCryptoAdapter — derivePMK + unwrapKey (Web Crypto via
 *                            @shn/envelope-crypto)
 *   IProjectionServiceClient — fetchWrappedKek
 *   IActiveSession           — loadUnwrappedKek; receives the
 *                              unwrapped KEK CryptoKey reference
 *
 * **Doctrinal load-bearing path:**
 *
 *   1. Patient submits knowledge factor → state machine moves to
 *      collecting-possession-factor; knowledge factor is held in
 *      flow state ONLY for the duration of the possession-factor
 *      collection step (replaced with empty string after PMK
 *      derivation initiates).
 *   2. Patient submits possession factor → state machine moves to
 *      deriving-pmk; orchestrator calls
 *      `IEnvelopeCryptoAdapter.derivePMK({ authFactor: knowledge,
 *      salt: pmkSalt })`.
 *   3. PMK derived (non-extractable CryptoKey held briefly in
 *      orchestrator local) → state machine moves to fetching-
 *      wrapped-kek; orchestrator calls
 *      `IProjectionServiceClient.fetchWrappedKek`.
 *   4. Wrapped KEK material fetched → orchestrator calls
 *      `IEnvelopeCryptoAdapter.unwrapKey({ outerKey: pmk,
 *      wrappedBytes: material.kekCiphertext, aad: kekAad,
 *      extractable: false })`. PMK reference dropped immediately
 *      after unwrap completes (orchestrator local goes out of
 *      scope; GC reclaims).
 *   5. KEK unwrapped → state machine moves to session-active;
 *      orchestrator calls `ActiveSession.loadUnwrappedKek({
 *      kekId, key })`. KEK reference now lives in the session.
 *
 * **Memory hygiene:**
 *
 *   - Knowledge factor (password string) — held in state machine
 *     `collecting-possession-factor` state; cleared after the
 *     orchestrator initiates `derivePMK`. JS string interning is
 *     unreliable for cleanup; load-bearing protection is the
 *     `extractable: false` PMK that the runtime never exposes.
 *   - PMK — non-extractable CryptoKey; runtime never exposes
 *     bytes. Reference held in orchestrator local during unwrap;
 *     dropped after.
 *   - KEK — non-extractable CryptoKey; loaded into ActiveSession
 *     via reference; session drops on `endSession()`.
 *
 * **Step-up matrix (PFO §2.2) — v1.5+.** v0 ships standard PHG
 * access (knowledge + possession). Sensitive-scope step-up
 * (delegation, emergency revocation, sensitive-category Consent
 * Artifact issuance, trusted-other enrollment all require fresh
 * MFA + 3 factors per PFO §2.2 table) lands as a refinement that
 * extends the state machine with inherence-factor / additional-
 * possession-factor branches.
 */
import { applyEvent, initialAuthState, isTerminal, } from './auth-state.js';
// ---------- Implementation ----------
export class AuthenticationFlow {
    options;
    state = initialAuthState();
    observers = new Set();
    constructor(options) {
        this.options = options;
    }
    getState() {
        return this.state;
    }
    subscribe(observer) {
        this.observers.add(observer);
        observer(this.state);
        return () => {
            this.observers.delete(observer);
        };
    }
    beginSignIn() {
        if (this.state.kind !== 'intro') {
            return;
        }
        this.transition({ kind: 'begin-sign-in' });
    }
    submitKnowledgeFactor(knowledgeFactor) {
        if (this.state.kind !== 'collecting-knowledge-factor') {
            return;
        }
        if (knowledgeFactor.length === 0) {
            this.transition({
                kind: 'fail',
                code: 'invalid-knowledge-factor',
                message: 'Password cannot be empty',
            });
            return;
        }
        this.transition({ kind: 'submit-knowledge-factor', knowledgeFactor });
    }
    async submitPossessionFactor(possessionFactor) {
        if (this.state.kind !== 'collecting-possession-factor') {
            return;
        }
        if (possessionFactor.length === 0) {
            this.transition({
                kind: 'fail',
                code: 'invalid-possession-factor',
                message: 'Verification code cannot be empty',
            });
            return;
        }
        // Verify possession factor (v0 stub: any non-empty accepts;
        // production wiring connects to SMS/TOTP/WebAuthn).
        const verifier = this.options.verifyPossessionFactor;
        if (verifier !== undefined) {
            try {
                const ok = await verifier(possessionFactor);
                if (!ok) {
                    this.transition({
                        kind: 'fail',
                        code: 'possession-factor-rejected',
                        message: 'Verification code did not match. Please try again.',
                    });
                    return;
                }
            }
            catch (e) {
                this.transition({
                    kind: 'fail',
                    code: 'possession-factor-verifier-failed',
                    message: errorMessage(e),
                });
                return;
            }
        }
        // Capture the knowledge factor before transitioning out of
        // 'collecting-possession-factor' (where it lives on the state).
        const knowledgeFactor = this.state.knowledgeFactor;
        this.transition({ kind: 'submit-possession-factor', possessionFactor });
        await this.runPmkAndUnwrap(knowledgeFactor);
    }
    signOut() {
        if (this.state.kind !== 'session-active') {
            return;
        }
        this.options.activeSession.endSession();
        this.transition({ kind: 'sign-out' });
    }
    restart() {
        if (!isTerminal(this.state)) {
            return;
        }
        this.transition({ kind: 'restart' });
    }
    // ---------- private ----------
    async runPmkAndUnwrap(knowledgeFactor) {
        let pmk;
        try {
            // Derive PMK. Cleartext password bytes live transiently in
            // the encoder buffer inside `derivePMK`; adapter zeroizes
            // them after derivation. PMK is non-extractable.
            const pmkOpts = this.options.pmkIterations !== undefined
                ? {
                    authFactor: knowledgeFactor,
                    salt: this.options.inputContext.pmkSalt,
                    iterations: this.options.pmkIterations,
                }
                : {
                    authFactor: knowledgeFactor,
                    salt: this.options.inputContext.pmkSalt,
                };
            pmk = await this.options.cryptoAdapter.derivePMK(pmkOpts);
        }
        catch (e) {
            this.transition({
                kind: 'fail',
                code: 'pmk-derivation-failed',
                message: errorMessage(e),
            });
            return;
        }
        this.transition({ kind: 'pmk-derived' });
        let material;
        try {
            material = await this.options.projectionService.fetchWrappedKek({
                subjectPciHash: this.options.inputContext.subjectPciHash,
                kekId: this.options.inputContext.kekId,
            });
        }
        catch (e) {
            this.transition({
                kind: 'fail',
                code: 'wrapped-kek-fetch-failed',
                message: errorMessage(e),
            });
            return;
        }
        if (material === null) {
            this.transition({
                kind: 'fail',
                code: 'wrapped-kek-not-found',
                message: 'No wrapped KEK found for the provided KEK identifier',
            });
            return;
        }
        this.transition({ kind: 'wrapped-kek-fetched', material });
        let kekHandle;
        try {
            const kek = await this.options.cryptoAdapter.unwrapKey({
                outerKey: pmk,
                wrappedBytes: material.kekCiphertext,
                aad: this.options.inputContext.kekAad,
                extractable: false,
            });
            kekHandle = { kekId: this.castKekId(material.kekId), key: kek };
        }
        catch (e) {
            this.transition({
                kind: 'fail',
                code: 'kek-unwrap-failed',
                message: errorMessage(e),
            });
            return;
        }
        finally {
            // Drop the PMK reference; runtime + GC reclaim the
            // non-extractable CryptoKey. Done.
            pmk = undefined;
        }
        // Load into ActiveSession + transition to session-active.
        this.options.activeSession.loadUnwrappedKek({
            kekId: kekHandle.kekId,
            key: kekHandle.key,
        });
        this.transition({ kind: 'kek-unwrapped', handle: kekHandle });
    }
    castKekId(id) {
        return id;
    }
    transition(event) {
        const next = applyEvent(this.state, event);
        if (next === this.state) {
            return;
        }
        this.state = next;
        for (const observer of this.observers) {
            observer(next);
        }
    }
}
function errorMessage(e) {
    if (e instanceof Error) {
        return e.message;
    }
    return String(e);
}
//# sourceMappingURL=auth-flow.js.map